/**
 * Timeout Trigger
 *
 * Opens popup after a specified delay.
 * Smart preloading: preloads iframe 3 seconds before the timeout fires.
 */

import type { Popup } from '../popup';
import { TimeoutPreloadStrategy } from '../preload-strategies/timeout-strategy';
import { createLogger } from '../../shared/logger';
import { TriggerStateBroadcaster } from './trigger-state-broadcaster';

const logger = createLogger('TimeoutTrigger');

/** How many ms before the trigger to start preloading */
const PRELOAD_BUFFER_MS = 3000;

export class TimeoutTrigger {
	private popup: Popup;
	private delay: number;
	private showTimeoutId: ReturnType<typeof setTimeout> | null = null;
	private preloadStrategy: TimeoutPreloadStrategy | null = null;
	private broadcaster: TriggerStateBroadcaster;

	constructor(popup: Popup, delay: number) {
		logger.log('constructor - delay:', delay, 'ms');
		this.popup = popup;
		this.delay = delay;
		this.broadcaster = new TriggerStateBroadcaster(
			popup.getFeedId(),
			'timeout'
		);
	}

	/**
	 * Initialize the trigger
	 */
	public init(): void {
		logger.log('init() called, delay:', this.delay);

		if (this.delay <= 0) {
			logger.log('Delay is <= 0, not setting timeout');
			return;
		}

		// Broadcast initial waiting state and start countdown
		this.broadcaster.setState('waiting');
		this.broadcaster.startCountdown(this.delay);

		// Calculate when to preload (3 seconds before show, minimum 0)
		const preloadDelay = Math.max(0, this.delay - PRELOAD_BUFFER_MS);

		logger.log(
			`Setting preload for ${preloadDelay}ms, show for ${this.delay}ms`
		);

		// Set up preload strategy
		if (preloadDelay < this.delay) {
			this.preloadStrategy = new TimeoutPreloadStrategy(preloadDelay);
			this.preloadStrategy.init(() => {
				logger.log('Preload strategy triggered');
				this.broadcaster.setState('preloading');
				this.popup.preload();
			});
		}

		// Set show timeout
		this.showTimeoutId = setTimeout(() => {
			logger.log('Show timeout fired! Calling popup.show()');
			this.broadcaster.stopCountdown();
			this.broadcaster.setState('triggered');
			this.popup.show();
		}, this.delay);
	}

	/**
	 * Destroy the trigger
	 */
	public destroy(): void {
		logger.log('destroy() called');
		this.broadcaster.destroy();
		this.preloadStrategy?.destroy();
		if (this.showTimeoutId !== null) {
			clearTimeout(this.showTimeoutId);
			this.showTimeoutId = null;
		}
	}
}
